// Preview mirrors the server's naming rules; the API generates the saved name.
export const ACTIVITY_TYPES = { lab: 'Lab', quiz: 'Quiz', midterm: 'Midterm', final: 'Final', custom: 'Other…' };
export const CUSTOM_TYPE_LIMIT = 40;

export function academicPrefix(lab) {
  const term = String(lab.term || '').trim();
  const shortTerm = /^1\d{2}[815]$/.test(term)
    ? `${{ 8: 'F', 1: 'W', 5: 'S' }[term[3]]}${term.slice(1, 3)}` : term;
  return [shortTerm, String(lab.courseCode || '').trim().toUpperCase(),
    lab.section ? `S${String(lab.section).trim()}` : ''].filter(Boolean).join('-');
}

export function namingFormValue(lab = {}) {
  return {
    activityType: lab.activityType || (!lab.isSemesterLab && lab.labNumber ? 'lab' : ''),
    activityNumber: String(lab.activityNumber || lab.labNumber || 1),
    customActivityType: lab.customActivityType || '',
  };
}

export function namingError(value) {
  if (!ACTIVITY_TYPES[value.activityType]) return 'Choose an activity type.';
  const number = Number(value.activityNumber);
  if (!/^[1-9]\d*$/.test(String(value.activityNumber)) || !Number.isSafeInteger(number)) {
    return 'Activity number must be a positive whole number.';
  }
  if (value.activityType === 'custom') {
    const label = value.customActivityType.normalize('NFC').trim();
    if (!label || label.length > CUSTOM_TYPE_LIMIT || !/^[\p{L}\p{M}]+(?:[ -]+[\p{L}\p{M}]+)*$/u.test(label)) {
      return `Custom type must be 1–${CUSTOM_TYPE_LIMIT} characters: letters, spaces or hyphens. Choose the number separately.`;
    }
    if (['lab', 'l', 'quiz', 'midterm', 'final', 'semester', 'other'].includes(label.toLowerCase())) {
      return 'Choose the matching activity type from the dropdown.';
    }
  }
  return '';
}

export function namingPayload(value) {
  return {
    activityType: value.activityType,
    activityNumber: Number(value.activityNumber),
    customActivityType: value.activityType === 'custom' ? value.customActivityType.normalize('NFC').trim().replace(/[ -]+/g, '-') : null,
  };
}

export function previewLabName(lab, value, semester = false) {
  if (!lab.courseCode?.trim()) return '';
  const prefix = academicPrefix(lab);
  if (semester) return `${prefix}-Semester`;
  if (namingError(value)) return `${prefix}-…`;
  const data = namingPayload(value);
  const suffix = data.activityType === 'lab' ? `L${data.activityNumber}`
    : `${data.activityType === 'custom' ? data.customActivityType : ACTIVITY_TYPES[data.activityType]}-${data.activityNumber}`;
  return `${prefix}-${suffix}`;
}

export function labLabel(lab) {
  if (lab.activityLabel) return lab.activityLabel;
  if (lab.activityType && lab.activityNumber) {
    return `${lab.activityType === 'custom' ? (lab.customActivityType || 'Activity').replace(/-/g, ' ') : ACTIVITY_TYPES[lab.activityType]} ${lab.activityNumber}`;
  }
  const fallback = lab.isSemesterLab ? 'Semester' : lab.labNumber ? `Lab ${lab.labNumber}` : 'Lab';
  const suffix = lab.isSemesterLab ? 'Semester' : `L${lab.labNumber}`;
  return lab.name && lab.name !== `${academicPrefix(lab)}-${suffix}` ? lab.name : fallback;
}

export function compareLabLabels(a, b) {
  if (!!a.isSemesterLab !== !!b.isSemesterLab) return a.isSemesterLab ? -1 : 1;
  return labLabel(a).localeCompare(labLabel(b), undefined, { numeric: true })
    || String(a.id || a._id).localeCompare(String(b.id || b._id));
}
